import type { CheckoutAddressFormData } from "@/modules/checkout/types"
import {
	ADDRESS_VALIDATION_CONFIG,
	ComponentType,
	ConfirmationLevel,
} from "./address-validation-config"

/**
 * 地址验证结果接口
 */
export interface AddressValidationResult {
	/** 是否验证通过 */
	isValid: boolean
	/** 错误信息多语言键列表 */
	issueKeys: string[]
	/** 建议的地址 */
	suggestedAddress?: CheckoutAddressFormData
	/** 是否应该显示推荐地址弹窗 */
	shouldShowSuggestionDialog: boolean
	/** 原始验证响应 */
	rawResponse?: GoogleAddressValidationResponse
}

/**
 * Google地址验证响应接口
 */
interface GoogleAddressValidationResponse {
	result: {
		verdict: {
			inputGranularity: string
			validationGranularity: string
			geocodeGranularity: string
			hasUnconfirmedComponents: boolean
			possibleNextAction: string
		}
		address: {
			formattedAddress: string
			postalAddress: {
				regionCode: string
				languageCode: string
				postalCode: string
				locality: string
				addressLines: string[]
			}
			addressComponents: Array<{
				componentName: {
					text: string
					languageCode: string
				}
				componentType: string
				confirmationLevel: string
				unexpected?: boolean
				inferred?: boolean
				spellCorrected?: boolean
				replaced?: boolean
			}>
			missingComponentTypes: string[]
			unconfirmedComponentTypes: string[]
		}
		geocode: {
			location: {
				latitude: number
				longitude: number
			}
			placeId: string
		}
	}
	responseId: string
}

/**
 * 地址验证服务
 * 使用Google Address Validation API验证地址的合法性
 */
export const AddressValidationService = {
	/**
	 * 验证地址
	 * @param addressData 地址数据
	 * @param currentAreaCode 当前区域代码
	 * @returns 验证结果
	 */
	async validateAddress(
		addressData: CheckoutAddressFormData,
		currentAreaCode: string,
	): Promise<AddressValidationResult> {
		try {
			// 构建验证请求
			const requestBody = AddressValidationService.buildValidationRequest(
				addressData,
				currentAreaCode,
			)

			// 调用Google API
			const response = await fetch(
				`${ADDRESS_VALIDATION_CONFIG.API_BASE_URL}?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(requestBody),
				},
			)

			if (!response.ok) {
				const errorData = await response.json()
				console.error("Google Address Validation API error:", errorData)

				// 如果API调用失败，返回模拟结果用于测试
				return AddressValidationService.createMockValidationResult(addressData)
			}

			const data: GoogleAddressValidationResponse = await response.json()

			// 处理验证响应
			return AddressValidationService.processValidationResponse(
				data,
				addressData,
			)
		} catch (error) {
			console.error("Address validation error:", error)

			// 发生错误时返回模拟结果
			return AddressValidationService.createMockValidationResult(addressData)
		}
	},

	/**
	 * 构建验证请求
	 * @param addressData 地址数据
	 * @param currentAreaCode 当前区域代码
	 * @returns 请求体
	 */
	buildValidationRequest(
		addressData: CheckoutAddressFormData,
		currentAreaCode: string,
	) {
		// 构建地址行数组 - 只包含必要的地址信息，与地址填写时的逻辑保持一致
		const addressLines = [
			addressData.address,
			addressData.addressDetail,
		].filter(Boolean)

		return {
			address: {
				regionCode: currentAreaCode,
				addressLines: addressLines,
				administrativeArea: addressData.province || undefined,
				locality: addressData.city || undefined,
				postalCode: addressData.zipCode || undefined,
			},
		}
	},

	/**
	 * 处理验证响应
	 * @param response Google API响应
	 * @param originalAddress 原始地址
	 * @returns 验证结果
	 */
	processValidationResponse(
		response: GoogleAddressValidationResponse,
		originalAddress: CheckoutAddressFormData,
	): AddressValidationResult {
		const { result } = response
		const { address } = result

		// 生成问题列表
		const issues = AddressValidationService.generateIssues(address)

		// 判断是否验证通过
		const isValid = issues.length === 0

		// 构建建议地址
		const suggestedAddress = AddressValidationService.buildSuggestedAddress(
			address,
			originalAddress,
		)

		// 判断是否应该显示推荐地址弹窗
		const shouldShowSuggestionDialog =
			AddressValidationService.shouldShowSuggestionDialog(address)

		return {
			isValid,
			issueKeys: issues,
			suggestedAddress,
			shouldShowSuggestionDialog,
			rawResponse: response,
		}
	},

	/**
	 * 分析地址组件
	 * @param address 地址对象
	 * @returns 组件分析结果
	 */
	analyzeAddressComponents(
		address: GoogleAddressValidationResponse["result"]["address"],
	) {
		// 这里可以根据需要添加更复杂的组件分析逻辑
		return {
			hasStreetNumber: !address.missingComponentTypes?.includes(
				ComponentType.STREET_NUMBER,
			),
			hasCity: !address.missingComponentTypes?.includes(ComponentType.LOCALITY),
			hasProvince: !address.missingComponentTypes?.includes(
				ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			),
			hasZipCode: !address.missingComponentTypes?.includes(
				ComponentType.POSTAL_CODE,
			),
		}
	},

	/**
	 * 生成问题列表
	 * 严格按照文档要求处理各种状态
	 */
	generateIssues(
		address: GoogleAddressValidationResponse["result"]["address"],
	): string[] {
		const issues: string[] = []
		const components = address.addressComponents || []

		// 特殊情况检查：城市和邮编的状态组合
		const cityComponent = components.find(
			(component) =>
				component.componentType === ComponentType.LOCALITY ||
				component.componentType === "postal_town", // 添加对 postal_town 的支持
		)
		const postalCodeComponent = components.find(
			(component) => component.componentType === ComponentType.POSTAL_CODE,
		)

		// 特殊情况1：城市是 UNCONFIRMED_AND_SUSPICIOUS，邮编是 CONFIRMED 且无补充信息
		if (
			cityComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS &&
			postalCodeComponent?.confirmationLevel === ConfirmationLevel.CONFIRMED &&
			!cityComponent.inferred &&
			!cityComponent.spellCorrected &&
			!cityComponent.replaced
		) {
			issues.push("order.addressValidation.cityNotConfirmed")
		}

		// 特殊情况2：城市是 UNCONFIRMED_AND_SUSPICIOUS，邮编是 UNCONFIRMED_BUT_PLAUSIBLE 且无改进建议
		if (
			cityComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS &&
			postalCodeComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE &&
			!cityComponent.inferred &&
			!cityComponent.spellCorrected &&
			!cityComponent.replaced &&
			!postalCodeComponent.inferred &&
			!postalCodeComponent.spellCorrected &&
			!postalCodeComponent.replaced
		) {
			issues.push("order.addressValidation.cityAndPostalCodeNotMatched")
		}

		// 如果已经处理了特殊情况，直接返回
		if (issues.length > 0) {
			return issues
		}

		// 1. 处理城市、邮编的 UNCONFIRMED_AND_SUSPICIOUS 状态
		if (
			address.unconfirmedComponentTypes?.includes(ComponentType.LOCALITY) ||
			address.unconfirmedComponentTypes?.includes("postal_town") // 添加对 postal_town 的支持
		) {
			const localityComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) =>
					c.componentType === ComponentType.LOCALITY ||
					c.componentType === ComponentType.POSTAL_TOWN,
			)
			if (
				localityComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS
			) {
				issues.push("order.addressValidation.cityNotConfirmed")
			}
		}

		if (
			address.unconfirmedComponentTypes?.includes(ComponentType.POSTAL_CODE)
		) {
			const postalComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.POSTAL_CODE,
			)
			if (
				postalComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS
			) {
				issues.push("order.addressValidation.postalCodeNotConfirmed")
			}
		}

		// 1.1. 处理城市、邮编的 UNCONFIRMED_BUT_PLAUSIBLE 状态
		// 处理城市组件
		if (address.unconfirmedComponentTypes?.includes(ComponentType.LOCALITY)) {
			const localityComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.LOCALITY,
			)
			if (
				localityComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
			) {
				// 如果不满足 inferred、spellCorrected、replaced 任意一个为 true，说明这个组件有问题
				if (
					!localityComponent.inferred &&
					!localityComponent.spellCorrected &&
					!localityComponent.replaced
				) {
					issues.push("order.addressValidation.cityNotConfirmed")
				}
			}
		}

		// 处理 postal_town 组件（英国地址常用）
		if (address.unconfirmedComponentTypes?.includes("postal_town")) {
			const postalTownComponents =
				address.addressComponents?.filter(
					(
						c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
					) => c.componentType === "postal_town",
				) || []

			// 检查是否有任何 postal_town 组件有问题
			const hasProblematicPostalTown = postalTownComponents.some(
				(component) => {
					// 如果是 UNCONFIRMED_BUT_PLAUSIBLE 且不满足改进条件
					if (
						component.confirmationLevel ===
						ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
					) {
						return (
							!component.inferred &&
							!component.spellCorrected &&
							!component.replaced
						)
					}
					// 如果是 CONFIRMED 但 unexpected 为 true
					if (
						component.confirmationLevel === ConfirmationLevel.CONFIRMED &&
						component.unexpected === true
					) {
						return true
					}
					return false
				},
			)

			if (hasProblematicPostalTown) {
				issues.push("order.addressValidation.cityNotConfirmed")
			}
		}

		if (
			address.unconfirmedComponentTypes?.includes(ComponentType.POSTAL_CODE)
		) {
			const postalComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.POSTAL_CODE,
			)
			if (
				postalComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
			) {
				// 如果不满足 inferred、spellCorrected、replaced 任意一个为 true，说明这个组件有问题
				if (
					!postalComponent.inferred &&
					!postalComponent.spellCorrected &&
					!postalComponent.replaced
				) {
					issues.push("order.addressValidation.postalCodeNotConfirmed")
				}
			}
		}

		// 2. 如果城市和邮编都有问题，显示组合错误信息
		// 直接根据校验接口返回值判断，而不是根据已生成的错误信息
		const hasCityIssue =
			AddressValidationService.hasCityValidationIssue(address)
		const hasPostalCodeIssue =
			AddressValidationService.hasPostalCodeValidationIssue(address)

		if (hasCityIssue && hasPostalCodeIssue) {
			return ["order.addressValidation.cityAndPostalCodeNotMatched"]
		}

		// 3. 处理缺失组件的兜底逻辑
		if (address.missingComponentTypes?.length > 0) {
			if (address.missingComponentTypes.includes(ComponentType.LOCALITY)) {
				issues.push("order.addressValidation.cityInvalid")
			}
			if (address.missingComponentTypes.includes(ComponentType.POSTAL_CODE)) {
				issues.push("order.addressValidation.postalCodeInvalid")
			}
			if (
				address.missingComponentTypes.includes(
					ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
				)
			) {
				issues.push("order.addressValidation.provinceMissingOrInvalid")
			}
			if (address.missingComponentTypes.includes(ComponentType.COUNTRY)) {
				issues.push("order.addressValidation.countryNotConfirmed")
			}
		}

		// 4. 如果城市和邮编都缺失，显示组合错误信息
		if (
			address.missingComponentTypes?.includes(ComponentType.LOCALITY) &&
			address.missingComponentTypes?.includes(ComponentType.POSTAL_CODE)
		) {
			return ["order.addressValidation.cannotVerifyAddress"]
		}

		// 5. 处理省的 UNCONFIRMED_AND_SUSPICIOUS 状态
		if (
			address.unconfirmedComponentTypes?.includes(
				ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
		) {
			const provinceComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
			if (
				provinceComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS
			) {
				issues.push("order.addressValidation.provinceSuspicious")
			}
		}

		// 6. 处理省的 UNCONFIRMED_BUT_PLAUSIBLE 状态
		if (
			address.unconfirmedComponentTypes?.includes(
				ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
		) {
			const provinceComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
			if (
				provinceComponent?.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
			) {
				// 如果不满足 inferred、spellCorrected、replaced 任意一个为 true，说明这个组件有问题
				if (
					!provinceComponent.inferred &&
					!provinceComponent.spellCorrected &&
					!provinceComponent.replaced
				) {
					issues.push("order.addressValidation.provinceNotConfirmed")
				}
			}
		}

		// 7. 处理省份 unexpected: true 的情况（文档中提到：我们内置的省的字段跟google地址库命名不一致,且google未识别）
		// 只在缺失省份信息时才走兜底报错
		if (
			address.missingComponentTypes?.includes(
				ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
		) {
			const provinceComponent = address.addressComponents?.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === ComponentType.ADMINISTRATIVE_AREA_LEVEL_1,
			)
			if (provinceComponent?.unexpected === true) {
				issues.push("order.addressValidation.provinceMissingOrInvalid")
			}
		}

		// 8. 处理其他地址组件问题（文档中提到：城市跟省填成一样的,可能存在缺失的情况）
		if (address.unconfirmedComponentTypes?.includes("point_of_interest")) {
			issues.push("order.addressValidation.cityInvalid")
		}

		// 返回第一个错误信息
		return issues.slice(0, 1)
	},

	/**
	 * 构建建议地址
	 * @param validatedAddress 验证后的地址
	 * @param originalAddress 原始地址
	 * @returns 建议的地址
	 */
	buildSuggestedAddress(
		validatedAddress: GoogleAddressValidationResponse["result"]["address"],
		originalAddress: CheckoutAddressFormData,
	): CheckoutAddressFormData {
		// 从验证后的地址中提取组件
		const components = validatedAddress.addressComponents || []

		const getComponent = (type: string) => {
			const component = components.find(
				(
					c: GoogleAddressValidationResponse["result"]["address"]["addressComponents"][number],
				) => c.componentType === type,
			)
			return component?.componentName?.text || ""
		}

		// 针对英国地址的特殊处理
		const getCityFromComponents = () => {
			// 优先尝试 postal_town（英国地址常用）
			let city = getComponent(ComponentType.POSTAL_TOWN)
			if (!city) {
				// 如果没有 postal_town，尝试 locality
				city = getComponent(ComponentType.LOCALITY)
			}
			// 如果还是没有，尝试从 formattedAddress 中提取
			if (!city && validatedAddress.formattedAddress) {
				// 简单的城市提取逻辑，可以根据需要优化
				const parts = validatedAddress.formattedAddress.split(",")
				if (parts.length > 1) {
					city = parts[parts.length - 2]?.trim() || ""
				}
			}
			const result = city || originalAddress.city
			return result
		}

		const getProvinceFromComponents = () => {
			// 优先尝试 administrative_area_level_1
			let province = getComponent(ComponentType.ADMINISTRATIVE_AREA_LEVEL_1)
			// 如果没有，尝试从 formattedAddress 中提取
			if (!province && validatedAddress.formattedAddress) {
				const parts = validatedAddress.formattedAddress.split(",")
				if (parts.length > 2) {
					province = parts[parts.length - 3]?.trim() || ""
				}
			}
			const result = province || originalAddress.province
			return result
		}

		const getZipCodeFromComponents = () => {
			// 优先尝试 postal_code
			let zipCode = getComponent(ComponentType.POSTAL_CODE)
			// 如果没有，尝试从 formattedAddress 中提取
			if (!zipCode && validatedAddress.formattedAddress) {
				const parts = validatedAddress.formattedAddress.split(",")
				if (parts.length > 1) {
					// 邮编通常在倒数第二个位置
					const potentialZipCode = parts[parts.length - 2]?.trim() || ""
					// 简单的邮编格式验证（英国邮编通常是字母+数字格式）
					if (/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(potentialZipCode)) {
						zipCode = potentialZipCode
					}
				}
			}
			const result = zipCode || originalAddress.zipCode
			return result
		}

		// 构建建议地址时，address 字段应该只包含街道地址，不包含省市邮编
		// 直接使用校验接口返回的 addressLines 中的值
		const getStreetAddressFromAddressLines = () => {
			if (
				validatedAddress.postalAddress?.addressLines &&
				validatedAddress.postalAddress.addressLines.length > 0
			) {
				// 使用 addressLines 中的第一个值作为街道地址
				return validatedAddress.postalAddress.addressLines[0]
			}
			return originalAddress.address
		}

		const suggestedAddress = {
			...originalAddress,
			address: getStreetAddressFromAddressLines(),
			city: getCityFromComponents(),
			province: getProvinceFromComponents(),
			zipCode: getZipCodeFromComponents(),
		}

		return suggestedAddress
	},

	/**
	 * 判断是否应该显示推荐地址弹窗
	 * 只有当城市或邮编的 ConfirmationLevel = UNCONFIRMED_BUT_PLAUSIBLE 或 CONFIRMED 或默认值，
	 * 且存在改进建议（inferred、spellCorrected、replaced 任一为 true）时才显示推荐地址弹窗
	 *
	 * 如果城市或邮编状态可疑且没有改进建议，则返回 false 显示修改弹窗
	 */
	shouldShowSuggestionDialog(
		address: GoogleAddressValidationResponse["result"]["address"],
	): boolean {
		const components = address.addressComponents || []

		// 获取城市和邮编组件
		const cityComponents = components.filter(
			(component) =>
				component.componentType === ComponentType.LOCALITY ||
				component.componentType === "postal_town", // 添加对 postal_town 的支持
		)
		const postalCodeComponent = components.find(
			(component) => component.componentType === ComponentType.POSTAL_CODE,
		)

		// 检查城市组件是否有改进建议（检查所有城市相关组件）
		const hasCityImprovements = cityComponents.some(
			(component) =>
				component.inferred === true ||
				component.spellCorrected === true ||
				component.replaced === true,
		)

		const hasPostalCodeImprovements =
			postalCodeComponent &&
			(postalCodeComponent.inferred === true ||
				postalCodeComponent.spellCorrected === true ||
				postalCodeComponent.replaced === true)

		// 检查城市组件的确认级别是否满足条件（检查所有城市相关组件）
		const cityLevelValid = cityComponents.some(
			(component) =>
				component.confirmationLevel ===
					ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE ||
				component.confirmationLevel === ConfirmationLevel.CONFIRMED ||
				!component.confirmationLevel, // 默认值
		)

		const postalCodeLevelValid =
			postalCodeComponent &&
			(postalCodeComponent.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE ||
				postalCodeComponent.confirmationLevel === ConfirmationLevel.CONFIRMED ||
				!postalCodeComponent.confirmationLevel) // 默认值

		// 特殊情况：如果城市或邮编状态可疑且没有改进建议，返回 false 显示修改弹窗
		if (
			cityComponents.length > 0 &&
			cityComponents.some(
				(component) =>
					component.confirmationLevel ===
						ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS &&
					!(
						component.inferred === true ||
						component.spellCorrected === true ||
						component.replaced === true
					),
			)
		) {
			return false
		}

		// 只有当城市或邮编的确认级别满足条件，且存在改进建议时才显示推荐地址弹窗
		// 如果没有改进建议，即使确认级别满足条件，也返回 false 显示修改弹窗
		const result = Boolean(
			(cityLevelValid && hasCityImprovements) ||
				(postalCodeLevelValid && hasPostalCodeImprovements),
		)

		return result
	},

	/**
	 * 检查城市是否有验证问题
	 * @param address 地址对象
	 * @returns 是否有城市验证问题
	 */
	hasCityValidationIssue(
		address: GoogleAddressValidationResponse["result"]["address"],
	): boolean {
		const components = address.addressComponents || []

		// 检查城市相关组件（LOCALITY 和 postal_town）
		const cityComponents = components.filter(
			(component) =>
				component.componentType === ComponentType.LOCALITY ||
				component.componentType === "postal_town",
		)

		// 检查是否有任何城市组件有问题
		return cityComponents.some((component) => {
			// 如果是 UNCONFIRMED_BUT_PLAUSIBLE 且不满足改进条件
			if (
				component.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
			) {
				return (
					!component.inferred &&
					!component.spellCorrected &&
					!component.replaced
				)
			}
			// 如果是 CONFIRMED 但 unexpected 为 true
			if (
				component.confirmationLevel === ConfirmationLevel.CONFIRMED &&
				component.unexpected === true
			) {
				return true
			}
			// 如果是 UNCONFIRMED_AND_SUSPICIOUS
			if (
				component.confirmationLevel ===
				ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS
			) {
				return true
			}
			return false
		})
	},

	/**
	 * 检查邮编是否有验证问题
	 * @param address 地址对象
	 * @returns 是否有邮编验证问题
	 */
	hasPostalCodeValidationIssue(
		address: GoogleAddressValidationResponse["result"]["address"],
	): boolean {
		const components = address.addressComponents || []

		// 检查邮编组件
		const postalCodeComponent = components.find(
			(component) => component.componentType === ComponentType.POSTAL_CODE,
		)

		if (!postalCodeComponent) {
			return false
		}

		// 如果是 UNCONFIRMED_BUT_PLAUSIBLE 且不满足改进条件
		if (
			postalCodeComponent.confirmationLevel ===
			ConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE
		) {
			return (
				!postalCodeComponent.inferred &&
				!postalCodeComponent.spellCorrected &&
				!postalCodeComponent.replaced
			)
		}
		// 如果是 CONFIRMED 但 unexpected 为 true
		if (
			postalCodeComponent.confirmationLevel === ConfirmationLevel.CONFIRMED &&
			postalCodeComponent.unexpected === true
		) {
			return true
		}
		// 如果是 UNCONFIRMED_AND_SUSPICIOUS
		if (
			postalCodeComponent.confirmationLevel ===
			ConfirmationLevel.UNCONFIRMED_AND_SUSPICIOUS
		) {
			return true
		}
		return false
	},

	/**
	 * 创建模拟的校验结果（用于测试）
	 * @param addressData 地址数据
	 * @returns 模拟验证结果
	 */
	createMockValidationResult(
		addressData: CheckoutAddressFormData,
	): AddressValidationResult {
		// 模拟一些地址校验逻辑
		const hasStreetNumber = /\d/.test(addressData.address)
		const hasCity = addressData.city.trim().length > 0
		const hasProvince = addressData.province.trim().length > 0
		const hasZipCode = addressData.zipCode.trim().length > 0

		// 简单的校验规则 - 为了测试失败状态，我们设置一个会失败的场景
		const issues: string[] = []

		if (!hasStreetNumber) {
			issues.push("order.addressValidation.missingComponents")
		}

		if (!hasCity) {
			issues.push("order.addressValidation.cityInvalid")
		}

		if (!hasProvince) {
			issues.push("order.addressValidation.provinceMissingOrInvalid")
		}

		if (!hasZipCode) {
			issues.push("order.addressValidation.postalCodeInvalid")
		}

		return {
			isValid: issues.length === 0,
			issueKeys: issues,
			suggestedAddress: addressData,
			shouldShowSuggestionDialog: false,
		}
	},
}
